import { useState, useEffect } from "react";
import { Film, Globe, Plus, Edit, Trash2, X, Search, CheckCircle2, XCircle, ExternalLink, Calendar, AlertTriangle, Clock, Inbox, Check, FileText, User, Mail, Building2, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ImageInputUploader } from "@/components/ImageInputUploader";

export function FestivalsAdminTab() {
  const [activeSubTab, setActiveSubTab] = useState("catalog"); // "catalog" | "submissions"
  const [festivals, setFestivals] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [search, setSearch] = useState("");
  const [regionFilter, setRegionFilter] = useState("all");
  const [sortBy, setSortBy] = useState("fecha_actualizacion_asc");

  // Modal State for Festival Edit/Create
  const [showModal, setShowModal] = useState(false);
  const [editingFest, setEditingFest] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    nombre: "",
    short_name: "",
    ciudad: "",
    pais: "",
    region: "LatAm",
    mes: "Junio",
    tipo: "Largometrajes, Cortometrajes, Documentales",
    estado: "Convocatoria Abierta",
    enlace_oficial: "",
    logo_url: "",
    banner_url: "",
    descripcion: ""
  });

  // Modal State for Viewing/Approving Submission
  const [selectedSub, setSelectedSub] = useState(null);
  const [processingSub, setProcessingSub] = useState(false);

  useEffect(() => {
    fetchFestivals();
    fetchSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionFilter, sortBy]);

  const fetchFestivals = async () => {
    setLoading(true);
    try {
      const params = { sort: sortBy };
      if (regionFilter !== "all") params.region = regionFilter;

      const { data } = await api.get("/festivals", { params });
      setFestivals(data);
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar festivales");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    setLoadingSubmissions(true);
    try {
      const { data } = await api.get("/admin/festival-submissions");
      setSubmissions(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const pendingSubmissions = submissions.filter(s => s.status === "pending");

  const filtered = festivals.filter(f => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const name = (f.nombre || f.name || "").toLowerCase();
    const city = (f.ciudad || f.city || "").toLowerCase();
    const country = (f.pais || f.country || "").toLowerCase();
    const desc = (f.descripcion || f.description || "").toLowerCase();
    return name.includes(q) || city.includes(q) || country.includes(q) || desc.includes(q);
  });

  const handleOpenModal = (fest = null) => {
    if (fest) {
      setEditingFest(fest);
      setFormData({
        nombre: fest.nombre || fest.name || "",
        short_name: fest.short_name || "",
        ciudad: fest.ciudad || fest.city || "",
        pais: fest.pais || fest.country || "",
        region: fest.region || "LatAm",
        mes: fest.mes || fest.month || "Junio",
        tipo: Array.isArray(fest.tipo || fest.category_types)
          ? (fest.tipo || fest.category_types).join(", ")
          : (fest.tipo || fest.category_types || ""),
        estado: fest.estado || (fest.call_open ? "Convocatoria Abierta" : "Cerrado"),
        enlace_oficial: fest.enlace_oficial || fest.website_url || "",
        logo_url: fest.logo_url || "",
        banner_url: fest.banner_url || fest.image_url || "",
        descripcion: fest.descripcion || fest.description || ""
      });
    } else {
      setEditingFest(null);
      setFormData({
        nombre: "",
        short_name: "",
        ciudad: "",
        pais: "",
        region: "LatAm",
        mes: "Junio",
        tipo: "Largometrajes, Cortometrajes, Documentales",
        estado: "Convocatoria Abierta",
        enlace_oficial: "",
        logo_url: "",
        banner_url: "",
        descripcion: ""
      });
    }
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        tipo: formData.tipo.split(",").map(s => s.trim()).filter(Boolean)
      };

      if (editingFest) {
        await api.put(`/admin/festivals/${editingFest.id}`, payload);
        toast.success("Festival actualizado exitosamente");
      } else {
        await api.post("/admin/festivals", payload);
        toast.success("Festival creado exitosamente");
      }

      setShowModal(false);
      fetchFestivals();
    } catch (err) {
      console.error(err);
      toast.error("Error al guardar festival");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`¿Seguro que deseas eliminar el festival "${name}"?`)) return;
    try {
      await api.delete(`/admin/festivals/${id}`);
      toast.success("Festival eliminado");
      fetchFestivals();
    } catch (err) {
      toast.error("Error al eliminar festival");
    }
  };

  const handleApproveSubmission = async (subId) => {
    setProcessingSub(true);
    try {
      const { data } = await api.post(`/admin/festival-submissions/${subId}/approve`);
      toast.success(data.message || "Festival aprobado y publicado en el catálogo");
      setSelectedSub(null);
      fetchFestivals();
      fetchSubmissions();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Error al aprobar la solicitud");
    } finally {
      setProcessingSub(false);
    }
  };

  const handleRejectSubmission = async (subId) => {
    if (!window.confirm("¿Estás seguro de marcar esta solicitud como rechazada?")) return;
    setProcessingSub(true);
    try {
      await api.post(`/admin/festival-submissions/${subId}/reject`);
      toast.info("Solicitud marcada como rechazada");
      setSelectedSub(null);
      fetchSubmissions();
    } catch (err) {
      console.error(err);
      toast.error("Error al rechazar solicitud");
    } finally {
      setProcessingSub(false);
    }
  };

  const handleDeleteSubmission = async (subId) => {
    if (!window.confirm("¿Deseas eliminar permanentemente esta solicitud?")) return;
    setProcessingSub(true);
    try {
      await api.delete(`/admin/festival-submissions/${subId}`);
      toast.success("Solicitud eliminada");
      setSelectedSub(null);
      fetchSubmissions();
    } catch (err) {
      toast.error("Error al eliminar solicitud");
    } finally {
      setProcessingSub(false);
    }
  };

  const isStaleDate = (dateStr) => {
    if (!dateStr) return true;
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = (now - date) / (1000 * 60 * 60 * 24);
    return diffDays > 180;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Sin fecha";
    const d = new Date(dateStr);
    return d.toLocaleDateString("es-ES", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="space-y-6">
      {/* Header Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Globe className="text-[#E11D48]" size={24} /> Directorio y Convocatorias de Festivales LGBTQ+
          </h1>
          <p className="text-zinc-400 text-xs mt-1">
            Revisa solicitudes de inclusión de comités y administra el catálogo oficial publicado.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => handleOpenModal()}
            className="bg-[#E11D48] hover:bg-[#be123c] text-white px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 transition-all shadow-md"
          >
            <Plus size={16} /> Crear Festival Directamente
          </button>
          <Link
            to="/festivales"
            target="_blank"
            className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 px-3.5 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors border border-white/10"
          >
            Ver Sitio Público →
          </Link>
        </div>
      </div>

      {/* Sub-Tabs Switcher */}
      <div className="flex items-center gap-2 border-b border-white/10 pb-2">
        <button
          onClick={() => setActiveSubTab("catalog")}
          className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
            activeSubTab === "catalog"
              ? "bg-[#E11D48] text-white shadow-lg"
              : "bg-zinc-900 text-zinc-400 hover:text-white border border-white/5"
          }`}
        >
          <Film size={15} /> Catálogo Publicado ({festivals.length})
        </button>

        <button
          onClick={() => setActiveSubTab("submissions")}
          className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 relative ${
            activeSubTab === "submissions"
              ? "bg-[#E11D48] text-white shadow-lg"
              : "bg-zinc-900 text-zinc-400 hover:text-white border border-white/5"
          }`}
        >
          <Inbox size={15} /> Solicitudes de Equipos
          {pendingSubmissions.length > 0 && (
            <span className="bg-amber-400 text-black px-2 py-0.5 rounded-full text-[10px] font-black animate-pulse">
              {pendingSubmissions.length} pendientes
            </span>
          )}
        </button>
      </div>

      {/* TAB 1: CATALOG */}
      {activeSubTab === "catalog" && (
        <div className="space-y-4">
          {/* Filter and Sorting Controls */}
          <div className="bg-zinc-900/80 border border-white/10 p-4 rounded-xl flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3 flex-1">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={15} className="absolute left-3 top-2.5 text-zinc-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por festival, ciudad o país..."
                  className="w-full bg-zinc-950 border border-white/10 rounded-lg pl-9 pr-3 py-1.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48]"
                />
              </div>

              <select
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
                className="bg-zinc-950 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-[#E11D48]"
              >
                <option value="all">Todas las Regiones</option>
                <option value="LatAm">América Latina (LatAm)</option>
                <option value="Europa">Europa</option>
                <option value="Norteamérica">Norteamérica</option>
                <option value="Asia/Oceanía">Asia / Oceanía</option>
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-zinc-950 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-amber-300 font-medium focus:outline-none focus:border-[#E11D48]"
              >
                <option value="fecha_actualizacion_asc">⏱ Ordenar: Más antiguo primero (detectar vencidos)</option>
                <option value="fecha_actualizacion_desc">✨ Ordenar: Recientemente actualizados</option>
              </select>
            </div>

            <div className="text-xs text-zinc-400">
              Total: <span className="text-white font-bold">{filtered.length}</span> registros
            </div>
          </div>

          {/* Table List */}
          <div className="bg-zinc-900/90 border border-white/10 rounded-xl overflow-hidden shadow-2xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-zinc-300">
                <thead className="bg-zinc-950/80 text-zinc-400 border-b border-white/10 uppercase tracking-wider text-[10px] font-bold">
                  <tr>
                    <th className="py-3.5 px-4">Festival</th>
                    <th className="py-3.5 px-4">Ubicación</th>
                    <th className="py-3.5 px-4">Mes</th>
                    <th className="py-3.5 px-4">Categorías</th>
                    <th className="py-3.5 px-4">Estado</th>
                    <th className="py-3.5 px-4">Última Actualización</th>
                    <th className="py-3.5 px-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-zinc-500">
                        Cargando directorio de festivales...
                      </td>
                    </tr>
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-zinc-500">
                        No se encontraron festivales que coincidan.
                      </td>
                    </tr>
                  ) : (
                    filtered.map(fest => {
                      const name = fest.nombre || fest.name;
                      const city = fest.ciudad || fest.city;
                      const country = fest.pais || fest.country;
                      const month = fest.mes || fest.month;
                      const types = fest.tipo || fest.category_types || [];
                      const isStale = isStaleDate(fest.fecha_actualizacion || fest.updated_at);
                      const status = fest.estado || (fest.call_open ? "Convocatoria Abierta" : "Cerrado");
                      const website = fest.enlace_oficial || fest.website_url;

                      return (
                        <tr key={fest.id} className="hover:bg-zinc-800/50 transition-colors">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <img
                                src={fest.banner_url || fest.image_url}
                                alt={name}
                                className="w-10 h-10 rounded-lg object-cover bg-zinc-950 border border-white/10"
                              />
                              <div>
                                <div className="font-extrabold text-white text-sm">{name}</div>
                                {fest.short_name && <span className="text-[10px] text-zinc-400 font-mono">{fest.short_name}</span>}
                              </div>
                            </div>
                          </td>

                          <td className="py-3 px-4 whitespace-nowrap">
                            <div className="font-semibold text-zinc-200">{city}, {country}</div>
                            <div className="text-[10px] text-zinc-500">{fest.region}</div>
                          </td>

                          <td className="py-3 px-4 whitespace-nowrap font-medium text-amber-400">
                            {month}
                          </td>

                          <td className="py-3 px-4 max-w-[200px]">
                            <div className="flex flex-wrap gap-1">
                              {types.map((t, i) => (
                                <span key={i} className="bg-zinc-800 px-2 py-0.5 rounded text-[10px] text-zinc-300 border border-white/5">
                                  {t}
                                </span>
                              ))}
                            </div>
                          </td>

                          <td className="py-3 px-4 whitespace-nowrap">
                            {status === "Convocatoria Abierta" ? (
                              <span className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-2.5 py-1 rounded-full text-[10px] font-bold inline-flex items-center gap-1">
                                <CheckCircle2 size={12} /> Convocatoria Abierta
                              </span>
                            ) : status === "Próxima Edición" ? (
                              <span className="bg-amber-500/20 text-amber-300 border border-amber-500/30 px-2.5 py-1 rounded-full text-[10px] font-semibold">
                                Próxima Edición
                              </span>
                            ) : (
                              <span className="bg-zinc-800 text-zinc-400 border border-white/10 px-2.5 py-1 rounded-full text-[10px] font-medium">
                                Cerrado
                              </span>
                            )}
                          </td>

                          <td className="py-3 px-4 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <Clock size={12} className={isStale ? "text-amber-400" : "text-zinc-500"} />
                              <span className={isStale ? "text-amber-300 font-semibold" : "text-zinc-400"}>
                                {formatDate(fest.fecha_actualizacion || fest.updated_at)}
                              </span>
                            </div>
                            {isStale && (
                              <div className="inline-flex items-center gap-1 text-[10px] text-amber-400 font-medium mt-0.5">
                                <AlertTriangle size={10} /> Requiere revisión
                              </div>
                            )}
                          </td>

                          <td className="py-3 px-4 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-2">
                              {website && (
                                <a
                                  href={website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1.5 text-zinc-400 hover:text-white bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                                  title="Sitio Oficial"
                                >
                                  <ExternalLink size={14} />
                                </a>
                              )}
                              <button
                                onClick={() => handleOpenModal(fest)}
                                className="p-1.5 text-zinc-300 hover:text-white bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                                title="Editar"
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                onClick={() => handleDelete(fest.id, name)}
                                className="p-1.5 text-rose-400 hover:text-rose-200 bg-rose-500/10 border border-rose-500/20 rounded-lg hover:bg-rose-500/20 transition-colors"
                                title="Eliminar"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: SUBMISSIONS */}
      {activeSubTab === "submissions" && (
        <div className="space-y-4">
          <div className="bg-zinc-900/90 border border-white/10 rounded-xl overflow-hidden shadow-2xl">
            <div className="p-4 border-b border-white/10 bg-zinc-950/60 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-white text-sm">Postulaciones Recibidas de Organizadores</h3>
                <p className="text-zinc-400 text-xs">Revisa los formularios enviados por comités de festivales antes de su aprobación.</p>
              </div>
              <button
                onClick={fetchSubmissions}
                className="text-xs text-zinc-400 hover:text-white bg-zinc-800 px-3 py-1.5 rounded-lg border border-white/10"
              >
                Actualizar Lista
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-zinc-300">
                <thead className="bg-zinc-950/80 text-zinc-400 border-b border-white/10 uppercase tracking-wider text-[10px] font-bold">
                  <tr>
                    <th className="py-3.5 px-4">Festival Postulado</th>
                    <th className="py-3.5 px-4">Ubicación / Región</th>
                    <th className="py-3.5 px-4">Contacto del Equipo</th>
                    <th className="py-3.5 px-4">Fecha Envío</th>
                    <th className="py-3.5 px-4">Estado</th>
                    <th className="py-3.5 px-4 text-right">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {loadingSubmissions ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-zinc-500">
                        Cargando postulaciones...
                      </td>
                    </tr>
                  ) : submissions.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-zinc-500">
                        No hay solicitudes de inclusión registradas aún.
                      </td>
                    </tr>
                  ) : (
                    submissions.map(sub => (
                      <tr key={sub.id} className="hover:bg-zinc-800/50 transition-colors">
                        <td className="py-3 px-4">
                          <div className="font-bold text-white text-sm">{sub.nombre}</div>
                          {sub.enlace_oficial && (
                            <a href={sub.enlace_oficial} target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline text-[11px] flex items-center gap-1 mt-0.5">
                              <ExternalLink size={11} /> {sub.enlace_oficial}
                            </a>
                          )}
                        </td>

                        <td className="py-3 px-4">
                          <div className="font-semibold text-zinc-200">{sub.ciudad}, {sub.pais}</div>
                          <div className="text-[10px] text-zinc-500">{sub.region} • {sub.mes}</div>
                        </td>

                        <td className="py-3 px-4">
                          <div className="font-semibold text-zinc-200">{sub.contacto_nombre || "Organizador"}</div>
                          <div className="text-[11px] text-zinc-400">{sub.contacto_email}</div>
                          {sub.contacto_cargo && <div className="text-[10px] text-zinc-500">{sub.contacto_cargo}</div>}
                        </td>

                        <td className="py-3 px-4 text-zinc-400 text-[11px]">
                          {formatDate(sub.created_at)}
                        </td>

                        <td className="py-3 px-4">
                          {sub.status === "pending" ? (
                            <span className="bg-amber-500/20 text-amber-300 border border-amber-500/30 px-2.5 py-1 rounded-full text-[10px] font-black inline-flex items-center gap-1">
                              <Clock size={12} /> Pendiente Revisión
                            </span>
                          ) : sub.status === "approved" ? (
                            <span className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-2.5 py-1 rounded-full text-[10px] font-bold inline-flex items-center gap-1">
                              <CheckCircle2 size={12} /> Aprobado y Publicado
                            </span>
                          ) : (
                            <span className="bg-zinc-800 text-zinc-500 border border-white/10 px-2.5 py-1 rounded-full text-[10px]">
                              Rechazado
                            </span>
                          )}
                        </td>

                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={() => setSelectedSub(sub)}
                            className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg text-xs font-semibold border border-white/10 transition-colors"
                          >
                            Revisar / Aprobar
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Reviewing and Approving Submissions */}
      {selectedSub && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-xl p-6 relative shadow-2xl my-8">
            <button
              onClick={() => setSelectedSub(null)}
              className="absolute top-4 right-4 text-zinc-400 hover:text-white p-2 rounded-xl hover:bg-zinc-800 transition-colors"
            >
              <X size={20} />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-[#E11D48]/20 border border-[#E11D48]/30 rounded-xl text-[#E11D48]">
                <FileText size={22} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">{selectedSub.nombre}</h3>
                <p className="text-zinc-400 text-xs">Solicitud de Inclusión enviada el {formatDate(selectedSub.created_at)}</p>
              </div>
            </div>

            <div className="space-y-4 text-xs">
              <div className="bg-zinc-950 p-4 rounded-xl border border-white/10 space-y-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-zinc-500 block">Ubicación:</span>
                    <strong className="text-white">{selectedSub.ciudad}, {selectedSub.pais} ({selectedSub.region})</strong>
                  </div>
                  <div>
                    <span className="text-zinc-500 block">Mes y Convocatoria:</span>
                    <strong className="text-amber-400">{selectedSub.mes} • {selectedSub.estado}</strong>
                  </div>
                </div>

                {selectedSub.enlace_oficial && (
                  <div>
                    <span className="text-zinc-500 block">Sitio Web Oficial:</span>
                    <a href={selectedSub.enlace_oficial} target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline break-all">
                      {selectedSub.enlace_oficial}
                    </a>
                  </div>
                )}

                <div>
                  <span className="text-zinc-500 block">Categorías Aceptadas:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(selectedSub.tipo || []).map((t, idx) => (
                      <span key={idx} className="bg-zinc-800 px-2 py-0.5 rounded text-zinc-200 border border-white/10">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>

                {selectedSub.descripcion && (
                  <div>
                    <span className="text-zinc-500 block">Descripción:</span>
                    <p className="text-zinc-300 leading-relaxed bg-zinc-900 p-2.5 rounded-lg border border-white/5 mt-1">
                      {selectedSub.descripcion}
                    </p>
                  </div>
                )}
              </div>

              {/* Contact Info */}
              <div className="bg-zinc-950 p-4 rounded-xl border border-white/10 space-y-2">
                <h4 className="font-bold text-zinc-300 text-xs uppercase tracking-wider flex items-center gap-1.5">
                  <User size={14} className="text-[#E11D48]" /> Contacto del Solicitante
                </h4>
                <div className="grid grid-cols-2 gap-3 text-zinc-300">
                  <div>
                    <span className="text-zinc-500 block">Nombre:</span>
                    <strong>{selectedSub.contacto_nombre || "No especificado"}</strong>
                  </div>
                  <div>
                    <span className="text-zinc-500 block">Email:</span>
                    <a href={`mailto:${selectedSub.contacto_email}`} className="text-blue-400 hover:underline font-mono">
                      {selectedSub.contacto_email}
                    </a>
                  </div>
                  {selectedSub.contacto_cargo && (
                    <div className="col-span-2">
                      <span className="text-zinc-500 block">Cargo:</span>
                      <span>{selectedSub.contacto_cargo}</span>
                    </div>
                  )}
                </div>
                {selectedSub.notas && (
                  <div className="pt-2 border-t border-white/10">
                    <span className="text-zinc-500 block">Notas adicionales:</span>
                    <p className="text-zinc-400 italic mt-0.5">{selectedSub.notas}</p>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-white/10">
                <button
                  onClick={() => handleDeleteSubmission(selectedSub.id)}
                  disabled={processingSub}
                  className="px-3.5 py-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 border border-rose-500/30 font-semibold rounded-xl text-xs transition-colors"
                >
                  Eliminar Solicitud
                </button>

                <div className="flex items-center gap-2">
                  {selectedSub.status === "pending" && (
                    <button
                      onClick={() => handleRejectSubmission(selectedSub.id)}
                      disabled={processingSub}
                      className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-semibold rounded-xl text-xs transition-colors"
                    >
                      Rechazar
                    </button>
                  )}

                  <button
                    onClick={() => handleApproveSubmission(selectedSub.id)}
                    disabled={processingSub}
                    className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs transition-all flex items-center gap-2 shadow-lg"
                  >
                    <Check size={16} />
                    {processingSub ? "Procesando..." : "Aprobar y Publicar en Catálogo"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Admin Edit/Create Festival */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-white/10 rounded-xl w-full max-w-2xl p-6 relative shadow-2xl my-8">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-zinc-400 hover:text-white p-1 rounded-lg"
            >
              <X size={20} />
            </button>

            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Film className="text-[#E11D48]" size={22} />
              {editingFest ? "Editar Festival LGBTQ+" : "Nuevo Festival de Cine LGBTQ+"}
            </h3>

            <form onSubmit={handleSave} className="space-y-4 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">Nombre del Festival *</label>
                  <input
                    required
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    placeholder="Ej: Frameline San Francisco"
                    className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                  />
                </div>

                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">Acrónimo / Sigla</label>
                  <input
                    value={formData.short_name}
                    onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                    placeholder="Ej: Frameline"
                    className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">Ciudad *</label>
                  <input
                    required
                    value={formData.ciudad}
                    onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })}
                    placeholder="Ej: San Francisco"
                    className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                  />
                </div>

                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">País *</label>
                  <input
                    required
                    value={formData.pais}
                    onChange={(e) => setFormData({ ...formData, pais: e.target.value })}
                    placeholder="Ej: Estados Unidos"
                    className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                  />
                </div>

                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">Región *</label>
                  <select
                    value={formData.region}
                    onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                    className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                  >
                    <option value="LatAm">América Latina (LatAm)</option>
                    <option value="Europa">Europa</option>
                    <option value="Norteamérica">Norteamérica</option>
                    <option value="Asia/Oceanía">Asia / Oceanía</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">Mes de Celebración</label>
                  <select
                    value={formData.mes}
                    onChange={(e) => setFormData({ ...formData, mes: e.target.value })}
                    className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                  >
                    {["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"].map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">Estado Convocatoria</label>
                  <select
                    value={formData.estado}
                    onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                    className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                  >
                    <option value="Convocatoria Abierta">Convocatoria Abierta</option>
                    <option value="Próxima Edición">Próxima Edición</option>
                    <option value="Cerrado">Cerrado</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-zinc-300 font-semibold mb-1">Categorías / Tipos (separadas por coma)</label>
                <input
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  placeholder="Largometrajes, Cortometrajes, Documentales, Trans/Queer"
                  className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                />
              </div>

              <div>
                <label className="block text-zinc-300 font-semibold mb-1">Sitio Web Oficial</label>
                <input
                  value={formData.enlace_oficial}
                  onChange={(e) => setFormData({ ...formData, enlace_oficial: e.target.value })}
                  placeholder="https://frameline.org"
                  className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                />
              </div>

              <div>
                <ImageInputUploader
                  label="Banner / Imagen Oficial del Festival"
                  value={formData.banner_url}
                  onChange={(val) => setFormData({ ...formData, banner_url: val })}
                  preset="festival"
                  aspectRatio="banner"
                  helperText="Sube una foto o introduce la URL oficial. Se optimizará y guardará en nuestro servidor como WebP ligero."
                />
              </div>

              <div>
                <label className="block text-zinc-300 font-semibold mb-1">Descripción / Misión</label>
                <textarea
                  rows={3}
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  placeholder="Resumen del festival, trayectoria e importancia..."
                  className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-medium rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 bg-[#E11D48] hover:bg-[#BE123C] text-white font-bold rounded-lg transition-colors"
                >
                  {saving ? "Guardando..." : "Guardar Festival"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
